/**
 * Guest Authentication Service
 * 
 * Handles guest user creation, authentication, and conversion to full user accounts.
 */

import { 
  generateDeviceFingerprint, 
  getOrGenerateFingerprint,
  storeGuestToken, 
  storeGuestUserId,
  getStoredGuestToken,
  getStoredGuestUserId,
  clearGuestData 
} from '../utils/guestFingerprint';

export interface GuestUser {
  id: number;
  display_name: string;
  guest_token: string;
  is_guest: true;
}

export interface GuestInitResponse {
  user_id: number;
  display_name: string;
  guest_token: string;
}

/**
 * Initialize a guest user (create or find existing)
 * 
 * @param displayName Guest's display name
 * @returns Promise<GuestUser | null> Guest user data or null if failed
 */
export async function initializeGuest(displayName: string): Promise<GuestUser | null> {
  try {
    // Check if we already have a guest token
    const existingToken = getStoredGuestToken();
    const existingUserId = getStoredGuestUserId();
    
    if (existingToken && existingUserId) {
      console.log('[Guest] Using existing guest session');
      return {
        id: existingUserId,
        display_name: displayName,
        guest_token: existingToken,
        is_guest: true
      };
    }

    // Generate device fingerprint
    const fingerprint = await getOrGenerateFingerprint();
    
    console.log('[Guest] Initializing new guest user:', displayName);
    
    // Note: Guest creation happens automatically when adding first item
    // This function just prepares the fingerprint
    return {
      id: 0, // Will be set when first item is added
      display_name: displayName,
      guest_token: '', // Will be set when first item is added
      is_guest: true
    };
    
  } catch (error) {
    console.error('[Guest] Failed to initialize guest:', error);
    return null;
  }
}

/**
 * Get current guest fingerprint
 * 
 * @returns Promise<string> Device fingerprint
 */
export async function getGuestFingerprint(): Promise<string> {
  return await getOrGenerateFingerprint();
}

/**
 * Store guest session data after item creation
 * 
 * @param userId Guest user ID
 * @param token Guest JWT token
 */
export function storeGuestSession(userId: number, token: string): void {
  storeGuestUserId(userId);
  storeGuestToken(token);
  console.log('[Guest] Stored guest session:', userId);
}

/**
 * Check if user is currently authenticated as guest
 * 
 * @returns boolean True if guest session exists
 */
export function isGuestAuthenticated(): boolean {
  return getStoredGuestToken() !== null && getStoredGuestUserId() !== null;
}

/**
 * Get current guest user ID
 * 
 * @returns number | null Guest user ID or null
 */
export function getCurrentGuestUserId(): number | null {
  return getStoredGuestUserId();
}

/**
 * Convert guest to full user account
 * 
 * @param email Email for new account
 * @param password Password for new account
 * @returns Promise<{success: boolean, user_id?: number, auth_token?: string, error?: string}>
 */
export async function convertGuestToUser(
  email: string, 
  password: string
): Promise<{
  success: boolean;
  user_id?: number;
  auth_token?: string;
  items_migrated?: number;
  error?: string;
}> {
  try {
    const guestToken = getStoredGuestToken();
    const guestUserId = getStoredGuestUserId();
    
    if (!guestToken || !guestUserId) {
      return {
        success: false,
        error: 'No guest session found'
      };
    }

    // TODO: Implement backend endpoint for guest conversion
    // const response = await fetch('/api/guests/convert', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //     'Authorization': `Bearer ${guestToken}`
    //   },
    //   body: JSON.stringify({ email, password })
    // });

    // if (!response.ok) {
    //   const error = await response.json();
    //   return { success: false, error: error.message };
    // }

    // const data = await response.json();
    
    // Clear guest data
    // clearGuestData();
    
    // return {
    //   success: true,
    //   user_id: data.user_id,
    //   auth_token: data.auth_token,
    //   items_migrated: data.items_migrated
    // };

    console.log('[Guest] Conversion endpoint not yet implemented');
    return {
      success: false,
      error: 'Conversion endpoint not yet implemented'
    };
    
  } catch (error) {
    console.error('[Guest] Failed to convert guest to user:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Clear guest session (logout)
 */
export function clearGuestSession(): void {
  clearGuestData();
  console.log('[Guest] Cleared guest session');
}

/**
 * Get guest display name from localStorage
 * 
 * @returns string | null Guest name if exists
 */
export function getGuestDisplayName(): string | null {
  try {
    return localStorage.getItem('tidy_guest_name');
  } catch (e) {
    console.error('Failed to get guest name from localStorage:', e);
    return null;
  }
}

/**
 * Store guest display name in localStorage
 * 
 * @param name Guest display name
 */
export function storeGuestDisplayName(name: string): void {
  try {
    localStorage.setItem('tidy_guest_name', name);
  } catch (e) {
    console.error('Failed to store guest name in localStorage:', e);
  }
}
